import React, {useEffect, useState} from 'react';
import NomenclatureTemplate from './NomenclatureTemplate';
import {useNavigate} from 'react-router-dom';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    Typography,
    CircularProgress,
    Link,
    Collapse,
    useTheme,
    useMediaQuery,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
} from '@mui/material';
import instance from '../../instance';
import CallNotification from '../../context/NotificationContext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {ExpandLess} from '@material-ui/icons';
import {IMerchant} from '../../interfaces/payment/IMerchant';
import {IReplicationNomenclature} from '../../interfaces/fetch/Nomenclature';

enum NomenclatureSource {
    SELECTED = 'selected',
    ALL = 'all',
}

enum ReplicationMode {
    NONE = 'none',
    REPLACE_ALL = 'replace_all',
    REPLACE_SELECTED = 'replace_selected',
    ADD_NEW = 'add_new',
}

enum GroupMode {
    NONE = 'none',
    WITH_GROUP = 'with_group',
    WITHOUT_GROUP = 'without_group',
}

export default function NomenclatureReplicationPage({setCurrentPageClass, userId, isAdmin}: IReplicationNomenclature) {
    const selectedRowsFromStorage: Array<{barcode: string; name: string; ntin?: string}> = JSON.parse(
        sessionStorage.getItem('nomenclatureSelectedRows') || '[]',
    );
    const navigate = useNavigate();
    const {handleErrorNotification} = CallNotification();

    // State
    const [source, setSource] = useState<NomenclatureSource>(
        selectedRowsFromStorage.length > 0 ? NomenclatureSource.SELECTED : NomenclatureSource.ALL,
    );
    const [merchants, setMerchants] = useState<IMerchant[]>([]);
    const [selectedMerchants, setSelectedMerchants] = useState<Set<number>>(new Set());
    const [replicationMode, setReplicationMode] = useState<ReplicationMode>(ReplicationMode.NONE);
    const [groupMode, setGroupMode] = useState<GroupMode>(GroupMode.NONE);
    const [isLoading, setIsLoading] = useState(false);
    const [isFetchingMerchants, setIsFetchingMerchants] = useState(false);
    const [isSelectedItemsExpanded, setIsSelectedItemsExpanded] = useState(false);

    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if (!isAdmin) {
            navigate('/nomenklatura');
        }
    }, [isAdmin, navigate]);

    // Загрузка списка мерчантов
    useEffect(() => {
        const fetchMerchants = async () => {
            if (!userId) return;
            setIsFetchingMerchants(true);
            try {
                const response = await instance.post('/LK', {
                    method: 'GetMerchantList',
                    params: {user_id: userId},
                });
                handleErrorNotification(response);

                const merchantList: IMerchant[] = (response.data?.merchant_list || [])
                    .filter((m: any) => m.id !== userId) // Исключаем самого ЦТО
                    .map((m: any) => ({
                        id: m.id,
                        name: m.name,
                        inn: m.inn,
                    }));

                setMerchants(merchantList);
            } catch (error) {
                console.error('Ошибка при загрузке списка мерчантов:', error);
            } finally {
                setIsFetchingMerchants(false);
            }
        };

        if (isAdmin) {
            void fetchMerchants();
        }
    }, [userId, isAdmin]);

    const handleMerchantToggle = (merchantId: number) => {
        setSelectedMerchants(prev => {
            const newSet = new Set(prev);
            if (newSet.has(merchantId)) {
                newSet.delete(merchantId);
            } else {
                newSet.add(merchantId);
            }
            return newSet;
        });
    };

    const handleSelectAll = () => {
        setSelectedMerchants(new Set(merchants.map(m => m.id)));
    };

    const handleDeselectAll = () => {
        setSelectedMerchants(new Set());
    };

    const isFormValid = selectedMerchants.size > 0 && replicationMode !== ReplicationMode.NONE && groupMode !== GroupMode.NONE;

    const handleReplicate = async () => {
        if (!isFormValid) return;

        setIsLoading(true);
        try {
            const response = await instance.post('/LK', {
                method: 'ReplicateNomenclature',
                params: {
                    user_id: userId,
                    source: source,
                    merchant_ids: Array.from(selectedMerchants),
                    mode: replicationMode,
                    group_mode: groupMode,
                    selected_barcodes: source === NomenclatureSource.SELECTED ? selectedRowsFromStorage.map(item => item.barcode) : undefined,
                },
            });

            handleErrorNotification(response, true);

            if (response.data?.error === 0) {
                // Успешно завершено
                navigate('/nomenklatura');
            }
        } catch (error) {
            console.error('Ошибка при тиражировании номенклатуры:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <NomenclatureTemplate setCurrentPageClass={setCurrentPageClass} isAdmin={isAdmin} userId={userId}>
            <Box sx={{maxWidth: 1000, mx: 'auto', p: 3}}>
                <Typography variant={isSm ? 'h5' : 'h4'} gutterBottom sx={{mb: isSm ? 2 : 3}}>
                    Тиражирование номенклатуры
                </Typography>
                <Typography variant="body2" sx={{mb: isSm ? 2 : 3}}>
                    Тиражирование позволяет скопировать номенклатуру из кабинета ЦТО в кабинеты выбранных клиентов. Выберите источник
                    номенклатуры, укажите клиентов для тиражирования и настройте дополнительные параметры.
                </Typography>

                {/* Шаг 1: Выбор источника */}
                <Paper elevation={2} sx={{p: 3, mb: 3}}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Шаг 1
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                        Выберите источник номенклатуры
                    </Typography>
                    <RadioGroup value={source} onChange={e => setSource(e.target.value as NomenclatureSource)}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                mb: 2,
                                // Флекс-ребёнок RadioGroup: без ограничения ширины карточку
                                // распирает nowrap-таблица списка, и она вылезает за границы шага.
                                minWidth: 0,
                                width: '100%',
                                maxWidth: '100%',
                                boxSizing: 'border-box',
                                cursor: selectedRowsFromStorage.length > 0 ? 'pointer' : 'not-allowed',
                                opacity: selectedRowsFromStorage.length > 0 ? 1 : 0.5,
                                border: source === NomenclatureSource.SELECTED ? '2px solid #1976d2' : '1px solid #e0e0e0',
                            }}
                            onClick={() => selectedRowsFromStorage.length > 0 && setSource(NomenclatureSource.SELECTED)}
                        >
                            <FormControlLabel
                                value={NomenclatureSource.SELECTED}
                                control={<Radio />}
                                disabled={selectedRowsFromStorage.length === 0}
                                label={
                                    <Box sx={{width: '100%'}}>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            Отмеченные позиции ({selectedRowsFromStorage.length})
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Будут тиражированы те позиции номенклатуры, которые вы отметили предварительно на вкладке
                                            «Номенклатура ЦТО»
                                        </Typography>
                                    </Box>
                                }
                            />
                            {selectedRowsFromStorage.length > 0 && (
                                <Box sx={{mt: 1, ml: 4}} onClick={e => e.stopPropagation()}>
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={e => {
                                            e.stopPropagation();
                                            setIsSelectedItemsExpanded(!isSelectedItemsExpanded);
                                        }}
                                        endIcon={isSelectedItemsExpanded ? <ExpandLess /> : <ExpandMoreIcon />}
                                        sx={{
                                            textTransform: 'none',
                                            p: 0,
                                            mt: 0.5,
                                        }}
                                    >
                                        {isSelectedItemsExpanded ? 'Скрыть список' : 'Показать список'}
                                    </Button>
                                    <Collapse in={isSelectedItemsExpanded}>
                                        <TableContainer
                                            sx={{
                                                mt: 1,
                                                maxHeight: 200,
                                                border: '1px solid #e0e4f0',
                                                borderRadius: '8px',
                                                backgroundColor: '#FFF',
                                            }}
                                        >
                                            <Table size="small" stickyHeader sx={{width: '100%', tableLayout: 'fixed'}}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{backgroundColor: '#f5f7ff', fontWeight: 600}}>Наименование</TableCell>
                                                        <TableCell sx={{backgroundColor: '#f5f7ff', fontWeight: 600, width: 150}}>
                                                            Штрихкод
                                                        </TableCell>
                                                        <TableCell sx={{backgroundColor: '#f5f7ff', fontWeight: 600, width: 160}}>
                                                            NTIN
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {selectedRowsFromStorage.map((item, index: number) => (
                                                        <TableRow
                                                            key={index}
                                                            sx={{
                                                                '&:last-child td, &:last-child th': {
                                                                    borderBottom: 0,
                                                                },
                                                            }}
                                                        >
                                                            <TableCell
                                                                title={item.name || ''}
                                                                sx={{
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                }}
                                                            >
                                                                {item.name || '—'}
                                                            </TableCell>
                                                            <TableCell sx={{fontFamily: 'monospace', whiteSpace: 'nowrap'}}>
                                                                {item.barcode || '—'}
                                                            </TableCell>
                                                            <TableCell sx={{fontFamily: 'monospace', whiteSpace: 'nowrap'}}>
                                                                {item.ntin || '—'}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Collapse>
                                </Box>
                            )}
                        </Paper>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                cursor: 'pointer',
                                border: source === NomenclatureSource.ALL ? '2px solid #1976d2' : '1px solid #e0e0e0',
                            }}
                            onClick={() => setSource(NomenclatureSource.ALL)}
                        >
                            <FormControlLabel
                                value={NomenclatureSource.ALL}
                                control={<Radio />}
                                label={
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            Тиражирование всей номенклатуры ЦТО
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Будет использован список всех позиций номенклатуры, который есть в кабинете ЦТО
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Paper>
                    </RadioGroup>
                </Paper>

                {/* Шаг 2: Выбор клиентов */}
                <Paper elevation={2} sx={{p: 3, mb: 3}}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Шаг 2
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                        Выберите клиентов, к которым будут применены изменения в номенклатуре
                    </Typography>

                    {isFetchingMerchants ? (
                        <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress />
                        </Box>
                    ) : merchants.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Нет доступных клиентов для тиражирования
                        </Typography>
                    ) : (
                        <>
                            <Box sx={{mb: 2, display: 'flex', gap: 2}}>
                                <Link component="button" variant="body2" onClick={handleSelectAll} sx={{cursor: 'pointer'}}>
                                    Выбрать всех
                                </Link>
                                <Link component="button" variant="body2" onClick={handleDeselectAll} sx={{cursor: 'pointer'}}>
                                    Снять все
                                </Link>
                            </Box>
                            <Stack spacing={1} sx={{maxHeight: 400, overflowY: 'auto'}}>
                                {merchants.map(merchant => (
                                    <FormControlLabel
                                        key={merchant.id}
                                        control={
                                            <Checkbox
                                                checked={selectedMerchants.has(merchant.id)}
                                                onChange={() => handleMerchantToggle(merchant.id)}
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {merchant.name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    ИИН/БИН: {merchant.inn}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                ))}
                            </Stack>
                        </>
                    )}
                </Paper>

                {/* Шаг 3: Дополнительные опции */}
                <Paper elevation={2} sx={{p: 3, mb: 3}}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Шаг 3
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                        Укажите опции тиражирования
                    </Typography>

                    <Typography variant="subtitle1" gutterBottom fontWeight={600} sx={{mt: 3}}>
                        Что делать с номенклатурой в кабинете клиента
                    </Typography>
                    <RadioGroup value={replicationMode} onChange={e => setReplicationMode(e.target.value as ReplicationMode)}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                mb: 2,
                                cursor: 'pointer',
                                border: replicationMode === ReplicationMode.REPLACE_SELECTED ? '2px solid #1976d2' : '1px solid #e0e0e0',
                            }}
                            onClick={() => setReplicationMode(ReplicationMode.REPLACE_SELECTED)}
                        >
                            <FormControlLabel
                                value={ReplicationMode.REPLACE_SELECTED}
                                control={<Radio />}
                                label={
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            Перезаписать тиражируемую
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Обновить только те позиции, которые совпадают по штрихкоду с тиражируемыми
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Paper>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                mb: 2,
                                cursor: 'pointer',
                                border: replicationMode === ReplicationMode.ADD_NEW ? '2px solid #1976d2' : '1px solid #e0e0e0',
                            }}
                            onClick={() => setReplicationMode(ReplicationMode.ADD_NEW)}
                        >
                            <FormControlLabel
                                value={ReplicationMode.ADD_NEW}
                                control={<Radio />}
                                label={
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            Дополнить новыми
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Добавить только те позиции, которых еще нет в номенклатуре клиента
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Paper>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                cursor: 'pointer',
                                border: replicationMode === ReplicationMode.REPLACE_ALL ? '2px solid #1976d2' : '1px solid #e0e0e0',
                            }}
                            onClick={() => setReplicationMode(ReplicationMode.REPLACE_ALL)}
                        >
                            <FormControlLabel
                                value={ReplicationMode.REPLACE_ALL}
                                control={<Radio />}
                                label={
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            Предварительно удалить всю номенклатуру
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Полностью перезаписать номенклатуру клиента
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Paper>
                    </RadioGroup>

                    <Typography variant="subtitle1" gutterBottom fontWeight={600} sx={{mt: 3}}>
                        Что делать с группами
                    </Typography>
                    <RadioGroup value={groupMode} onChange={e => setGroupMode(e.target.value as GroupMode)}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                mb: 2,
                                cursor: 'pointer',
                                border: groupMode === GroupMode.WITH_GROUP ? '2px solid #1976d2' : '1px solid #e0e0e0',
                            }}
                            onClick={() => setGroupMode(GroupMode.WITH_GROUP)}
                        >
                            <FormControlLabel
                                value={GroupMode.WITH_GROUP}
                                control={<Radio />}
                                label={
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            Перенести номенклатуру с группой
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Номенклатура будет перенесена вместе с группами, к которым она принадлежит
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Paper>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                cursor: 'pointer',
                                border: groupMode === GroupMode.WITHOUT_GROUP ? '2px solid #1976d2' : '1px solid #e0e0e0',
                            }}
                            onClick={() => setGroupMode(GroupMode.WITHOUT_GROUP)}
                        >
                            <FormControlLabel
                                value={GroupMode.WITHOUT_GROUP}
                                control={<Radio />}
                                label={
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            Перенести без группы
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Номенклатура будет перенесена без привязки к группам
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Paper>
                    </RadioGroup>
                </Paper>

                {/* Кнопка запуска */}
                <Box sx={{display: 'flex', justifyContent: 'flex-start', gap: 2}}>
                    <Button
                        variant="contained"
                        onClick={() => void handleReplicate()}
                        disabled={!isFormValid || isLoading}
                        startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
                    >
                        {isLoading ? 'Тиражирование...' : 'Тиражировать'}
                    </Button>
                    <Button variant="outlined" onClick={() => navigate('/nomenklatura')} disabled={isLoading}>
                        Отмена
                    </Button>
                </Box>
            </Box>
        </NomenclatureTemplate>
    );
}
